// NGRP Official Party Emblem — Meaning & Vision (trilingual data).
//
// This file is intentionally content-heavy so the Emblem page component can
// stay lean. All strings are provided in English, Hindi and Marathi. Consumers
// pick the correct locale via `pickLang(obj, lang)` with English fallback.
//
// IMPORTANT: This artwork is the NGRP Official Party Emblem/Logo. It is NOT
// automatically the NGRP election symbol. Election symbols are allotted by the
// Election Commission of India under the Election Symbols (Reservation and
// Allotment) Order. Until an election symbol is formally allotted/approved,
// this artwork should be labelled as the NGRP Official Party Emblem/Logo.

export function pickLang(obj, lang) {
  if (!obj) return obj;
  if (obj[lang] !== undefined) return obj[lang];
  return obj.en;
}

// ---------------------------------------------------------------------------
// HERO INTRO
// ---------------------------------------------------------------------------
export const EMBLEM_INTRO = {
  en: {
    kicker: 'NGRP Logo — Meaning & Vision',
    title: 'Governance philosophy, in visual form.',
    body: 'The NGRP emblem represents our commitment to building a new era of transparent, accountable and citizen-centric governance for a stronger and developed India. Every element — the rising sun, the gear, the modern skyline, the people at the centre, the open book, the Ashoka Chakra and the laurel leaves — stands for a specific reform value.',
    motto: 'GOOD GOVERNANCE  •  STRONG INDIA  •  BRIGHTER FUTURE',
    panel_alt: 'NGRP Official Party Emblem — Meaning & Vision panel',
  },
  hi: {
    kicker: 'एनजीआरपी लोगो — अर्थ और दृष्टि',
    title: 'शासन का दर्शन, दृश्य रूप में।',
    body: 'एनजीआरपी का प्रतीक चिह्न पारदर्शी, जवाबदेह और नागरिक-केंद्रित शासन के एक नए युग के निर्माण की हमारी प्रतिबद्धता को दर्शाता है — एक सशक्त और विकसित भारत के लिए। उगता सूरज, गियर, आधुनिक क्षितिज, केंद्र में जनता, खुली पुस्तक, अशोक चक्र और लॉरेल पत्ते — हर तत्व एक विशिष्ट सुधार मूल्य का प्रतीक है।',
    motto: 'सुशासन  •  सशक्त भारत  •  उज्ज्वल भविष्य',
    panel_alt: 'एनजीआरपी आधिकारिक पार्टी प्रतीक — अर्थ और दृष्टि पैनल',
  },
  mr: {
    kicker: 'एनजीआरपी लोगो — अर्थ आणि दृष्टी',
    title: 'शासनाचे तत्त्वज्ञान, दृश्य स्वरूपात.',
    body: 'एनजीआरपीचे बोधचिन्ह पारदर्शक, जबाबदार आणि नागरिक-केंद्रित शासनाच्या नव्या युगाच्या उभारणीच्या आमच्या बांधिलकीचे प्रतिनिधित्व करते — एका सशक्त आणि विकसित भारतासाठी. उगवता सूर्य, गियर, आधुनिक क्षितिज, केंद्रस्थानी जनता, उघडे पुस्तक, अशोक चक्र आणि लॉरेल पाने — प्रत्येक घटक एका विशिष्ट सुधार मूल्याचे प्रतीक आहे.',
    motto: 'सुशासन  •  सशक्त भारत  •  उज्ज्वल भविष्य',
    panel_alt: 'एनजीआरपी अधिकृत पक्ष बोधचिन्ह — अर्थ आणि दृष्टी पॅनेल',
  },
};

// ---------------------------------------------------------------------------
// 7 SYMBOL ELEMENTS
// tone is a hex used for the icon accent (kept language-agnostic).
// ---------------------------------------------------------------------------
const _ELEMENT_META = [
  { key: 'sun',    icon: 'Sunrise',   tone: '#F47B20' },
  { key: 'gear',   icon: 'Cog',       tone: '#062B5C' },
  { key: 'city',   icon: 'Building2', tone: '#062B5C' },
  { key: 'people', icon: 'Users',     tone: '#F47B20' },
  { key: 'book',   icon: 'BookOpen',  tone: '#138A4B' },
  { key: 'chakra', icon: 'Compass',   tone: '#062B5C' },
  { key: 'laurel', icon: 'Leaf',      tone: '#138A4B' },
];

const _ELEMENT_TEXT = {
  en: {
    sun:    { title: 'Rising Sun',        subtitle: 'A New Dawn',              body: 'A new dawn of good governance, hope, energy and a brighter future for every citizen.' },
    gear:   { title: 'Gear',              subtitle: 'Systems & Reforms',       body: 'Represents systems, efficiency and institutional reforms that drive sustainable development — governance that runs on process, not personality.' },
    city:   { title: 'Developed India',   subtitle: 'Modern Infrastructure',   body: 'Modern infrastructure and economic growth that create opportunities for all — cities, industry, digital public services and dignified livelihoods.' },
    people: { title: 'People at the Centre', subtitle: 'Citizens are the Power', body: 'Citizens are the true power. NGRP exists to serve people and empower them to shape India’s future — government works for the citizen, not the other way around.' },
    book:   { title: 'Book',              subtitle: 'Knowledge & Constitution', body: 'Knowledge, education and awareness build an informed society and responsible leadership. The two pages of the book stand for Constitution & Values (left) and Knowledge & Education (right).' },
    chakra: { title: 'Ashoka Chakra',     subtitle: 'Dharma & Progress',       body: 'Upholds the Constitution, justice, dharma and continuous progress in national life — the wheel of law and the promise of forward motion.' },
    laurel: { title: 'Laurel Leaves',     subtitle: 'Peace & National Pride',  body: 'Symbolise peace, unity, strength, national pride and our commitment to protect the environment — saffron and green, side by side.' },
  },
  hi: {
    sun:    { title: 'उगता सूरज',           subtitle: 'नई भोर',                 body: 'सुशासन की नई भोर — हर नागरिक के लिए आशा, ऊर्जा और उज्ज्वल भविष्य।' },
    gear:   { title: 'गियर',                subtitle: 'व्यवस्था और सुधार',       body: 'व्यवस्था, दक्षता और संस्थागत सुधार का प्रतीक — शासन जो व्यक्ति पर नहीं, प्रक्रिया पर चले।' },
    city:   { title: 'विकसित भारत',          subtitle: 'आधुनिक बुनियादी ढाँचा',    body: 'आधुनिक बुनियादी ढाँचा और आर्थिक विकास जो सभी के लिए अवसर पैदा करें — शहर, उद्योग, डिजिटल सेवाएँ और सम्मानजनक जीवन।' },
    people: { title: 'केंद्र में जनता',        subtitle: 'नागरिक ही असली शक्ति',    body: 'नागरिक ही सच्ची शक्ति हैं। एनजीआरपी जनता की सेवा और सशक्तिकरण के लिए है — सरकार नागरिक के लिए काम करती है, इसका उलटा नहीं।' },
    book:   { title: 'पुस्तक',              subtitle: 'ज्ञान और संविधान',         body: 'ज्ञान, शिक्षा और जागरूकता से ही सूचित समाज और जवाबदेह नेतृत्व बनता है। पुस्तक के दो पन्ने — संविधान और मूल्य (बायाँ), ज्ञान और शिक्षा (दायाँ)।' },
    chakra: { title: 'अशोक चक्र',            subtitle: 'धर्म और प्रगति',           body: 'संविधान, न्याय, धर्म और राष्ट्रीय जीवन में निरंतर प्रगति का प्रतीक — विधि का चक्र और आगे बढ़ने का संकल्प।' },
    laurel: { title: 'लॉरेल पत्ते',           subtitle: 'शांति और राष्ट्रीय गौरव',    body: 'शांति, एकता, शक्ति, राष्ट्रीय गौरव और पर्यावरण की रक्षा की प्रतिबद्धता का प्रतीक — केसरिया और हरा, साथ-साथ।' },
  },
  mr: {
    sun:    { title: 'उगवता सूर्य',          subtitle: 'नवी पहाट',               body: 'सुशासनाची नवी पहाट — प्रत्येक नागरिकासाठी आशा, ऊर्जा आणि उज्ज्वल भविष्य.' },
    gear:   { title: 'गियर',                subtitle: 'व्यवस्था आणि सुधारणा',    body: 'व्यवस्था, कार्यक्षमता आणि संस्थात्मक सुधारणांचे प्रतीक — शासन व्यक्तीवर नाही, प्रक्रियेवर चालावे.' },
    city:   { title: 'विकसित भारत',          subtitle: 'आधुनिक पायाभूत सुविधा',    body: 'आधुनिक पायाभूत सुविधा आणि आर्थिक विकास जे सर्वांसाठी संधी निर्माण करतात — शहरे, उद्योग, डिजिटल सेवा आणि सन्माननीय जगणे.' },
    people: { title: 'केंद्रस्थानी जनता',       subtitle: 'नागरिक हीच खरी शक्ती',    body: 'नागरिक हीच खरी शक्ती आहे. एनजीआरपी जनतेच्या सेवेसाठी आणि सक्षमीकरणासाठी आहे — सरकार नागरिकांसाठी काम करते, उलट नाही.' },
    book:   { title: 'पुस्तक',              subtitle: 'ज्ञान आणि संविधान',        body: 'ज्ञान, शिक्षण आणि जागरूकतेतून सुजाण समाज आणि जबाबदार नेतृत्व घडते. पुस्तकाची दोन पाने — संविधान आणि मूल्ये (डावे), ज्ञान आणि शिक्षण (उजवे).' },
    chakra: { title: 'अशोक चक्र',            subtitle: 'धर्म आणि प्रगती',          body: 'संविधान, न्याय, धर्म आणि राष्ट्रीय जीवनातील अखंड प्रगतीचे प्रतीक — कायद्याचे चक्र आणि पुढे जाण्याचा संकल्प.' },
    laurel: { title: 'लॉरेल पाने',           subtitle: 'शांती आणि राष्ट्रीय अभिमान', body: 'शांती, एकता, सामर्थ्य, राष्ट्रीय अभिमान आणि पर्यावरण रक्षणाच्या बांधिलकीचे प्रतीक — भगवा आणि हिरवा, बरोबरीने.' },
  },
};

function _buildElements(lang) {
  const text = _ELEMENT_TEXT[lang] || _ELEMENT_TEXT.en;
  return _ELEMENT_META.map((m) => ({
    ...m,
    ...(text[m.key] || _ELEMENT_TEXT.en[m.key]),
  }));
}

export const EMBLEM_ELEMENTS = {
  en: _buildElements('en'),
  hi: _buildElements('hi'),
  mr: _buildElements('mr'),
};

// ---------------------------------------------------------------------------
// 5 PILLARS AT THE BOTTOM (Hindi labels retained + English gloss)
// Each pillar has: en_title (English), local_title (Devanagari), line (short body)
// ---------------------------------------------------------------------------
const _PILLAR_META = [
  { key: 'seva',           tone: '#062B5C', local: 'जन सेवा' },
  { key: 'vikas',          tone: '#F47B20', local: 'विकास' },
  { key: 'pardarshita',    tone: '#138A4B', local: 'पारदर्शिता' },
  { key: 'jawabdehi',      tone: '#062B5C', local: 'जवाबदेही' },
  { key: 'satat_bhavishya',tone: '#138A4B', local: 'सतत भविष्य' },
];

const _PILLAR_TEXT = {
  en: {
    seva:            { title: 'Service to People',     line: 'Government exists to serve every citizen — with dignity, speed and respect.' },
    vikas:           { title: 'Development',           line: 'Broad-based development that reaches every region, every profession, every household.' },
    pardarshita:     { title: 'Transparency',          line: 'Public money, public decisions and public performance — all publicly visible.' },
    jawabdehi:       { title: 'Accountability',        line: 'Every authority answerable for its decisions and its delivery.' },
    satat_bhavishya: { title: 'Sustainable Future',    line: 'A prosperous India that protects its environment, water, agriculture and climate.' },
  },
  hi: {
    seva:            { title: 'जन सेवा',                line: 'सरकार का उद्देश्य हर नागरिक की सम्मानजनक, तेज़ और आदरपूर्ण सेवा।' },
    vikas:           { title: 'विकास',                  line: 'हर क्षेत्र, हर पेशे और हर परिवार तक पहुँचने वाला व्यापक विकास।' },
    pardarshita:     { title: 'पारदर्शिता',              line: 'सार्वजनिक धन, निर्णय और प्रदर्शन — सब कुछ जनता के सामने।' },
    jawabdehi:       { title: 'जवाबदेही',              line: 'हर पद, हर संस्था अपने निर्णय और अपने परिणामों के लिए ज़िम्मेदार।' },
    satat_bhavishya: { title: 'सतत भविष्य',             line: 'ऐसा समृद्ध भारत जो पर्यावरण, जल, कृषि और जलवायु को सुरक्षित रखे।' },
  },
  mr: {
    seva:            { title: 'जन सेवा',                line: 'सरकारचा उद्देश प्रत्येक नागरिकाची सन्मानजनक, जलद आणि आदरपूर्वक सेवा.' },
    vikas:           { title: 'विकास',                  line: 'प्रत्येक प्रदेश, प्रत्येक व्यवसाय आणि प्रत्येक कुटुंबापर्यंत पोहोचणारा व्यापक विकास.' },
    pardarshita:     { title: 'पारदर्शकता',              line: 'सार्वजनिक निधी, निर्णय आणि कामगिरी — सर्व काही जनतेसमोर.' },
    jawabdehi:       { title: 'जबाबदारी',              line: 'प्रत्येक पद, प्रत्येक संस्था आपल्या निर्णयांसाठी आणि निष्पत्तीसाठी उत्तरदायी.' },
    satat_bhavishya: { title: 'शाश्वत भविष्य',           line: 'एक असा समृद्ध भारत जो पर्यावरण, जल, शेती आणि हवामान जपतो.' },
  },
};

function _buildPillars(lang) {
  const text = _PILLAR_TEXT[lang] || _PILLAR_TEXT.en;
  return _PILLAR_META.map((m) => ({
    ...m,
    ...(text[m.key] || _PILLAR_TEXT.en[m.key]),
  }));
}

export const EMBLEM_PILLARS = {
  en: _buildPillars('en'),
  hi: _buildPillars('hi'),
  mr: _buildPillars('mr'),
};

// ---------------------------------------------------------------------------
// WHAT THE LOGO COMMUNICATES
// ---------------------------------------------------------------------------
export const EMBLEM_COMMUNICATES = {
  en: [
    { title: 'Constitution & Values', body: 'The scale of justice reflects equality, rights and rule of law — the constitutional foundation of every NGRP decision.' },
    { title: 'Knowledge & Education', body: 'The light bulb stands for ideas, innovation and enlightened leadership — governance guided by evidence, not slogans.' },
    { title: 'Five Pillars at the Bottom', body: 'Service, Development, Transparency, Accountability and a Sustainable Future — our core promises to every citizen.' },
    { title: 'Tricolour Theme', body: 'Inspired by the Indian flag: courage (saffron), purity and peace (white) and growth (green), with the Ashoka Chakra for righteousness and movement.' },
    { title: 'Circle', body: 'Symbolises unity, inclusiveness and a national vision that leaves no one behind.' },
    { title: 'Tagline', body: 'Our ultimate goal — good governance today for a strong and prosperous India tomorrow.' },
  ],
  hi: [
    { title: 'संविधान और मूल्य', body: 'न्याय का तराज़ू समानता, अधिकार और विधि के शासन का प्रतीक है — एनजीआरपी के हर निर्णय का संवैधानिक आधार।' },
    { title: 'ज्ञान और शिक्षा', body: 'बल्ब विचार, नवाचार और प्रबुद्ध नेतृत्व का प्रतीक है — नारे नहीं, तथ्यों पर आधारित शासन।' },
    { title: 'पाँच स्तंभ (नीचे)', body: 'सेवा, विकास, पारदर्शिता, जवाबदेही और सतत भविष्य — हर नागरिक के लिए हमारे मूल वादे।' },
    { title: 'तिरंगा थीम', body: 'भारतीय ध्वज से प्रेरित — साहस (केसरिया), शुद्धता व शांति (श्वेत), विकास (हरा) और अशोक चक्र धर्म व गति का प्रतीक।' },
    { title: 'वृत्त', body: 'एकता, समावेशिता और ऐसे राष्ट्रीय दृष्टिकोण का प्रतीक जो किसी को पीछे न छोड़े।' },
    { title: 'ध्येयवाक्य', body: 'हमारा अंतिम लक्ष्य — आज सुशासन ताकि कल भारत सशक्त और समृद्ध हो।' },
  ],
  mr: [
    { title: 'संविधान आणि मूल्ये', body: 'न्यायाचा तराजू समता, हक्क आणि कायद्याच्या राज्याचे प्रतीक आहे — एनजीआरपीच्या प्रत्येक निर्णयाचा संवैधानिक पाया.' },
    { title: 'ज्ञान आणि शिक्षण', body: 'बल्ब कल्पना, नवोन्मेष आणि प्रबुद्ध नेतृत्वाचे प्रतीक — घोषवाक्यांवर नाही, पुराव्यांवर आधारित शासन.' },
    { title: 'पाच स्तंभ (खाली)', body: 'सेवा, विकास, पारदर्शकता, जबाबदारी आणि शाश्वत भविष्य — प्रत्येक नागरिकासाठीचे आमचे मुख्य वचन.' },
    { title: 'तिरंगा थीम', body: 'भारतीय ध्वजापासून प्रेरित — शौर्य (भगवा), शुद्धता व शांती (श्वेत), विकास (हिरवा) आणि अशोक चक्र धर्म व गतीचे प्रतीक.' },
    { title: 'वर्तुळ', body: 'ऐक्य, समावेशकता आणि कोणालाही मागे न सोडणाऱ्या राष्ट्रीय दृष्टीचे प्रतीक.' },
    { title: 'ब्रीदवाक्य', body: 'आमचे अंतिम ध्येय — आज सुशासन जेणेकरून उद्या भारत सशक्त आणि समृद्ध बनेल.' },
  ],
};

// ---------------------------------------------------------------------------
// COLOURS & MEANING
// ---------------------------------------------------------------------------
const _COLOUR_META = [
  { key: 'saffron',   swatch: '#F47B20', textOn: 'white' },
  { key: 'white',     swatch: '#FFFFFF', textOn: '#0B1220' },
  { key: 'green',     swatch: '#138A4B', textOn: 'white' },
  { key: 'navy',      swatch: '#062B5C', textOn: 'white' },
];

const _COLOUR_TEXT = {
  en: {
    saffron: { title: 'Saffron', body: 'Courage, sacrifice, selfless service and national pride.' },
    white:   { title: 'White',   body: 'Truth, peace, transparency and purity of intent.' },
    green:   { title: 'Green',   body: 'Growth, agriculture, harmony, sustainability and prosperity.' },
    navy:    { title: 'Navy Blue', body: 'Trust, stability, responsibility and strength of institutions.' },
  },
  hi: {
    saffron: { title: 'केसरिया',   body: 'साहस, त्याग, निःस्वार्थ सेवा और राष्ट्रीय गौरव।' },
    white:   { title: 'श्वेत',      body: 'सत्य, शांति, पारदर्शिता और नीयत की पवित्रता।' },
    green:   { title: 'हरा',       body: 'विकास, कृषि, सद्भाव, सततता और समृद्धि।' },
    navy:    { title: 'गहरा नीला', body: 'विश्वास, स्थिरता, ज़िम्मेदारी और संस्थाओं की शक्ति।' },
  },
  mr: {
    saffron: { title: 'भगवा',      body: 'शौर्य, त्याग, निःस्वार्थ सेवा आणि राष्ट्रीय अभिमान.' },
    white:   { title: 'श्वेत',      body: 'सत्य, शांती, पारदर्शकता आणि हेतूची शुद्धता.' },
    green:   { title: 'हिरवा',     body: 'विकास, शेती, सुसंवाद, शाश्वतता आणि समृद्धी.' },
    navy:    { title: 'गडद निळा', body: 'विश्वास, स्थिरता, जबाबदारी आणि संस्थांचे सामर्थ्य.' },
  },
};

function _buildColours(lang) {
  const text = _COLOUR_TEXT[lang] || _COLOUR_TEXT.en;
  return _COLOUR_META.map((m) => ({
    ...m,
    ...(text[m.key] || _COLOUR_TEXT.en[m.key]),
  }));
}

export const EMBLEM_COLOURS = {
  en: _buildColours('en'),
  hi: _buildColours('hi'),
  mr: _buildColours('mr'),
};

// ---------------------------------------------------------------------------
// PURPOSE / PROMISE / SLOGAN
// ---------------------------------------------------------------------------
export const EMBLEM_MISSION_TRIO = {
  en: {
    purpose: {
      title: 'Our Purpose',
      body: 'To transform India’s governance through integrity, accountability, innovation and people’s participation.',
    },
    promise: {
      title: 'Our Promise',
      body: 'Transparent systems. Accountable leadership. Inclusive growth. Equal opportunities. Stronger India. Better future.',
    },
    slogan: {
      title: 'Our Slogan',
      body: 'GOOD GOVERNANCE.  STRONG INDIA.  BRIGHTER FUTURE.',
    },
  },
  hi: {
    purpose: {
      title: 'हमारा उद्देश्य',
      body: 'ईमानदारी, जवाबदेही, नवाचार और जन-भागीदारी के माध्यम से भारत के शासन को बदलना।',
    },
    promise: {
      title: 'हमारा वादा',
      body: 'पारदर्शी व्यवस्था। जवाबदेह नेतृत्व। समावेशी विकास। समान अवसर। सशक्त भारत। बेहतर भविष्य।',
    },
    slogan: {
      title: 'हमारा ध्येयवाक्य',
      body: 'सुशासन।  सशक्त भारत।  उज्ज्वल भविष्य।',
    },
  },
  mr: {
    purpose: {
      title: 'आमचा उद्देश',
      body: 'प्रामाणिकपणा, जबाबदारी, नवोन्मेष आणि जनसहभागाद्वारे भारताच्या शासनात परिवर्तन घडवणे.',
    },
    promise: {
      title: 'आमचे वचन',
      body: 'पारदर्शक व्यवस्था. जबाबदार नेतृत्व. समावेशी विकास. समान संधी. सशक्त भारत. उज्ज्वल भविष्य.',
    },
    slogan: {
      title: 'आमचे ब्रीदवाक्य',
      body: 'सुशासन.  सशक्त भारत.  उज्ज्वल भविष्य.',
    },
  },
};

// ---------------------------------------------------------------------------
// OVERALL MESSAGE (quote shown on a navy card)
// ---------------------------------------------------------------------------
export const EMBLEM_MESSAGE = {
  en: '“Constitution at the foundation. Knowledge in leadership. Technology in administration. Accountability in execution. Prosperity as the outcome.”',
  hi: '“आधार में संविधान। नेतृत्व में ज्ञान। प्रशासन में तकनीक। निष्पादन में जवाबदेही। परिणाम में समृद्धि।”',
  mr: '“पायात संविधान. नेतृत्वात ज्ञान. प्रशासनात तंत्रज्ञान. अंमलबजावणीत जबाबदारी. निष्पत्तीत समृद्धी.”',
};

// ---------------------------------------------------------------------------
// ECI DISCLAIMER
// ---------------------------------------------------------------------------
export const EMBLEM_ECI_NOTICE = {
  en: {
    heading: 'Emblem vs. Election Symbol — an important distinction',
    body: 'This artwork is the NGRP Official Party Emblem/Logo. It should not be described as the party’s official election symbol. Election symbols in India are governed and allotted by the Election Commission of India under the Election Symbols (Reservation and Allotment) Order. The ECI separately publishes information on free/common/reserved symbols. Until an election symbol is formally allotted or approved by the ECI, all NGRP documentation labels this design as the “NGRP Official Party Emblem/Logo”.',
    link_label: 'Election Commission of India — Election Symbols',
    link_href: 'https://eci.gov.in/political-parties/election-symbols/',
  },
  hi: {
    heading: 'प्रतीक चिह्न बनाम चुनाव चिह्न — एक महत्वपूर्ण अंतर',
    body: 'यह कलाकृति एनजीआरपी का आधिकारिक पार्टी प्रतीक चिह्न/लोगो है। इसे पार्टी का आधिकारिक चुनाव चिह्न नहीं कहा जाना चाहिए। भारत में चुनाव चिह्नों का निर्धारण और आवंटन भारत निर्वाचन आयोग द्वारा चुनाव चिह्न (आरक्षण और आवंटन) आदेश के अंतर्गत किया जाता है। जब तक भारत निर्वाचन आयोग द्वारा एक चुनाव चिह्न औपचारिक रूप से आवंटित या स्वीकृत नहीं हो जाता, तब तक एनजीआरपी के सभी दस्तावेज़ों में इसे “एनजीआरपी आधिकारिक पार्टी प्रतीक चिह्न/लोगो” के रूप में ही उल्लिखित किया जाता है।',
    link_label: 'भारत निर्वाचन आयोग — चुनाव चिह्न',
    link_href: 'https://eci.gov.in/political-parties/election-symbols/',
  },
  mr: {
    heading: 'बोधचिन्ह विरुद्ध निवडणूक चिन्ह — एक महत्त्वाचा फरक',
    body: 'ही कलाकृती एनजीआरपीचे अधिकृत पक्ष बोधचिन्ह/लोगो आहे. तिला पक्षाचे अधिकृत निवडणूक चिन्ह म्हणून वर्णन करू नये. भारतातील निवडणूक चिन्हांचे नियमन आणि वाटप भारत निवडणूक आयोग निवडणूक चिन्ह (आरक्षण आणि वाटप) आदेशांतर्गत करते. भारत निवडणूक आयोगाकडून निवडणूक चिन्ह औपचारिकपणे वाटप किंवा मंजूर होईपर्यंत, एनजीआरपीच्या सर्व दस्तऐवजांमध्ये या रचनेला “एनजीआरपी अधिकृत पक्ष बोधचिन्ह/लोगो” असेच म्हटले जाईल.',
    link_label: 'भारत निवडणूक आयोग — निवडणूक चिन्हे',
    link_href: 'https://eci.gov.in/political-parties/election-symbols/',
  },
};

// ---------------------------------------------------------------------------
// SECTION LABELS (eyebrows, titles, subtitles used on the Emblem page)
// ---------------------------------------------------------------------------
export const EMBLEM_LABELS = {
  en: {
    elements_kicker: 'Symbol elements',
    elements_title: 'What each part of the emblem means',
    elements_sub: 'Seven elements. One governance philosophy.',
    communicates_kicker: 'Design intent',
    communicates_title: 'What the logo communicates',
    communicates_lede: 'Every choice in the NGRP emblem was intentional. Together, these six ideas describe the kind of republic NGRP is trying to help build.',
    mark_caption: 'The circular emblem is used as the NGRP mark across the site, on documents and in party communications.',
    colours_kicker: 'Colours & meaning',
    colours_title: 'The palette of the republic',
    colours_sub: 'Colours drawn from the Indian tricolour and the language of institutions. Every shade carries a value.',
    pillars_kicker: 'The five pillars',
    pillars_title: 'What NGRP promises every citizen',
    pillars_sub: 'These five pillars appear at the base of the emblem — in both Devanagari and English — as our institutional promises.',
    pillar_label: 'Pillar',
    overall_label: 'The overall message',
    mission_kicker: 'Our purpose, promise & slogan',
    mission_title: 'Three sentences that summarise NGRP',
    mission_sub: 'Everything in the emblem — the sun, the gear, the city, the people, the book, the chakra and the laurel — points to these three commitments.',
    important_label: 'Important',
  },
  hi: {
    elements_kicker: 'प्रतीक तत्व',
    elements_title: 'प्रतीक चिह्न के हर हिस्से का अर्थ',
    elements_sub: 'सात तत्व। एक शासन दर्शन।',
    communicates_kicker: 'डिज़ाइन का उद्देश्य',
    communicates_title: 'लोगो क्या कहता है',
    communicates_lede: 'एनजीआरपी के प्रतीक में हर चयन उद्देश्यपूर्ण है। ये छह विचार मिलकर उस गणराज्य का वर्णन करते हैं जिसे एनजीआरपी बनाने में सहायक होना चाहता है।',
    mark_caption: 'गोलाकार प्रतीक चिह्न वेबसाइट, दस्तावेज़ों और पार्टी संचार में एनजीआरपी की पहचान के रूप में उपयोग होता है।',
    colours_kicker: 'रंग और अर्थ',
    colours_title: 'गणराज्य की रंग-पट्टिका',
    colours_sub: 'भारतीय तिरंगे और संस्थाओं की भाषा से लिए गए रंग। हर रंग एक मूल्य का प्रतीक है।',
    pillars_kicker: 'पाँच स्तंभ',
    pillars_title: 'एनजीआरपी हर नागरिक से क्या वादा करता है',
    pillars_sub: 'ये पाँच स्तंभ प्रतीक चिह्न के आधार पर — देवनागरी और अंग्रेज़ी दोनों में — हमारे संस्थागत वचनों के रूप में अंकित हैं।',
    pillar_label: 'स्तंभ',
    overall_label: 'समग्र संदेश',
    mission_kicker: 'हमारा उद्देश्य, वादा और ध्येयवाक्य',
    mission_title: 'तीन वाक्य जो एनजीआरपी को परिभाषित करते हैं',
    mission_sub: 'प्रतीक चिह्न के सूरज, गियर, शहर, जनता, पुस्तक, चक्र और लॉरेल — सब इन्हीं तीन प्रतिबद्धताओं की ओर संकेत करते हैं।',
    important_label: 'महत्वपूर्ण',
  },
  mr: {
    elements_kicker: 'बोधचिन्हातील घटक',
    elements_title: 'बोधचिन्हाच्या प्रत्येक भागाचा अर्थ',
    elements_sub: 'सात घटक. एक शासन तत्त्वज्ञान.',
    communicates_kicker: 'रचनेचा हेतू',
    communicates_title: 'लोगो काय सांगतो',
    communicates_lede: 'एनजीआरपीच्या बोधचिन्हातील प्रत्येक निवड हेतुपुरस्सर आहे. या सहा कल्पना एकत्रित पणे तो प्रजासत्ताक साकारण्याचे वर्णन करतात, ज्याच्या उभारणीत एनजीआरपीला मदत करायची आहे.',
    mark_caption: 'गोलाकार बोधचिन्ह वेबसाइट, दस्तऐवज आणि पक्षाच्या संवादात एनजीआरपीची ओळख म्हणून वापरले जाते.',
    colours_kicker: 'रंग आणि अर्थ',
    colours_title: 'प्रजासत्ताकाची रंगसंगती',
    colours_sub: 'भारतीय तिरंग्यातून आणि संस्थांच्या भाषेतून घेतलेले रंग. प्रत्येक रंग एका मूल्याचा प्रतीक आहे.',
    pillars_kicker: 'पाच स्तंभ',
    pillars_title: 'एनजीआरपी प्रत्येक नागरिकाला काय वचन देते',
    pillars_sub: 'हे पाच स्तंभ बोधचिन्हाच्या तळाशी — देवनागरी आणि इंग्रजी दोन्हींत — आमच्या संस्थात्मक वचनांच्या रूपात कोरलेले आहेत.',
    pillar_label: 'स्तंभ',
    overall_label: 'एकत्रित संदेश',
    mission_kicker: 'आमचा उद्देश, वचन आणि ब्रीदवाक्य',
    mission_title: 'एनजीआरपीचे सार तीन वाक्यांत',
    mission_sub: 'बोधचिन्हातील सूर्य, गियर, शहर, जनता, पुस्तक, चक्र आणि लॉरेल — सर्व याच तीन बांधिलक्यांकडे बोट दाखवतात.',
    important_label: 'महत्त्वाचे',
  },
};
